import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon, Mic as MicIcon, Pause as PauseIcon, PlayArrow as PlayIcon, Stop as StopIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { formatDuration } from "../../utils/format";
import { AudioRecorder } from "../../lib/recorder";

export interface DirectAudioRecorderProps {
  onAccept?: (blob: Blob, duration: number) => void;
  onCancel?: () => void;
  initialBlob?: Blob | null;
  showUseThisButton?: boolean;
  showDoneButton?: boolean;
}

enum RecorderState {
  IDLE = "idle",
  RECORDING = "recording",
  RECORDED = "recorded",
  PLAYING = "playing",
}

export const DirectAudioRecorder: React.FC<DirectAudioRecorderProps> = ({
  onAccept,
  onCancel,
  initialBlob = null,
  showUseThisButton = false,
  showDoneButton = false,
}) => {
  const [audioBlob, setAudioBlob] = useState<Blob | null>(initialBlob || null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [state, setState] = useState<RecorderState>(
    initialBlob ? RecorderState.RECORDED : RecorderState.IDLE
  );
  const [duration, setDuration] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [audioLevel, setAudioLevel] = useState<number[]>([]);
  const [microphoneError, setMicrophoneError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const animationRef = useRef<number | null>(null);
  const recorderRef = useRef<AudioRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  const setupRecorder = useCallback(async () => {
    console.log("Setting up recorder...");
    setIsInitializing(true);
    setMicrophoneError(null);

    try {
      // Check permissions first
      await AudioRecorder.probeForPermissions();
      
      // Create audio context for visualization
      const context = new AudioContext();
      audioContextRef.current = context;
      
      // Initialize recorder
      recorderRef.current = new AudioRecorder();
      await recorderRef.current.ready;
      
      // Set up audio analysis
      if (recorderRef.current) {
        const source = await recorderRef.current.tap(context);
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;
      }

      console.log("Recorder setup complete");
    } catch (error) {
      console.error("Error setting up recorder:", error);
      setMicrophoneError("Could not access microphone. Please check your browser permissions.");
    } finally {
      setIsInitializing(false);
    }
  }, []);

  // Audio level visualization function
  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Calculate average level from frequency data, focusing on vocal frequencies
    // Human voice typically falls between 100Hz-3000Hz
    const vocalRangeStart = Math.floor(dataArray.length * 0.1); // ~100Hz
    const vocalRangeEnd = Math.floor(dataArray.length * 0.3);   // ~3000Hz
    
    let sum = 0;
    let count = 0;
    for (let i = vocalRangeStart; i < vocalRangeEnd; i++) {
      sum += dataArray[i];
      count++;
    }
    const avg = count > 0 ? sum / count : 0;
    
    // Apply some smoothing and amplification
    const amplificationFactor = 1.5;
    const smoothedAvg = Math.min(255, avg * amplificationFactor);
    
    // Keep the last 15 values
    const newLevels = [...audioLevel, smoothedAvg];
    if (newLevels.length > 15) {
      newLevels.shift();
    }
    
    setAudioLevel(newLevels);
    animationRef.current = requestAnimationFrame(updateAudioLevel);
  }, [audioLevel]);

  useEffect(() => {
    setupRecorder();
    
    // Handle initial blob if provided
    if (initialBlob) {
      const url = URL.createObjectURL(initialBlob);
      setAudioUrl(url);
      
      // Get duration
      const audio = new Audio();
      audio.src = url;
      audio.addEventListener("loadedmetadata", () => {
        setDuration(audio.duration);
      });
    }
    
    return () => {
      // Cleanup
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (recorderRef.current) {
        recorderRef.current.ensureStopped();
      }
    };
  }, [initialBlob]);

  const startRecording = useCallback(async () => {
    if (!recorderRef.current) {
      console.error("Recorder not initialized");
      return;
    }

    console.log("Starting recording...");

    try {
      // Create a new recorder instance for each recording
      recorderRef.current = new AudioRecorder();
      await recorderRef.current.ready;
      
      // Reset state
      setAudioBlob(null);
      setAudioUrl(null);
      setAudioLevel([]);
      setState(RecorderState.RECORDING);
      
      // Start timer
      const startTime = Date.now();
      timerRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 500);
      
      // Start audio visualization
      animationRef.current = requestAnimationFrame(updateAudioLevel);
      
    } catch (error) {
      console.error("Error recording:", error);
      setMicrophoneError("Recording failed. Please try again.");
      setState(RecorderState.IDLE);
    }
  }, [updateAudioLevel]);

  const stopRecording = useCallback(async () => {
    if (!recorderRef.current) return;
    
    try {
      await recorderRef.current.ensureStopped();
      await recorderRef.current.stopped;
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Get the recorded blob and set it
      const blob = await recorderRef.current.blob;
      setAudioBlob(blob);
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setDuration(elapsedTime);
      setState(RecorderState.RECORDED);

      // Auto-accept if showUseThisButton is false
      if (!showUseThisButton && onAccept) {
        onAccept(blob, elapsedTime);
      }
      
    } catch (error) {
      console.error("Error stopping recording:", error);
      setState(RecorderState.IDLE);
    }
  }, [elapsedTime, showUseThisButton, onAccept]);

  const playRecording = useCallback(() => {
    if (!audioRef.current || !audioUrl) return;
    
    audioRef.current.play().catch(err => {
      console.error("Error playing audio:", err);
    });
    setState(RecorderState.PLAYING);
  }, [audioUrl]);

  const pauseRecording = useCallback(() => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    setState(RecorderState.RECORDED);
  }, []);

  const resetRecording = useCallback(() => {
    // Clear all state
    setAudioBlob(null);
    setState(RecorderState.IDLE);
    setDuration(0);
    setElapsedTime(0);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [audioUrl]);

  const acceptRecording = useCallback(() => {
    if (audioBlob && onAccept) {
      onAccept(audioBlob, duration);
    }
  }, [audioBlob, duration, onAccept]);

  // Helper to generate waveform bars
  const renderWaveform = useCallback(() => {
    const minBarHeight = 3;
    const maxBarHeight = 24;
    
    if (audioLevel.length === 0) {
      // Default static waveform when no data
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 30, 
          width: '100%', 
          mt: 1, 
          mb: 2 
        }}>
          {[...Array(15)].map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 3,
                height: 3,
                mx: 0.5,
                backgroundColor: '#ef4444',
                borderRadius: 1
              }}
            />
          ))}
        </Box>
      );
    }
    
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: 30, 
        width: '100%', 
        mt: 1, 
        mb: 2 
      }}>
        {audioLevel.map((level, i) => {
          // Scale the audio level with a more dynamic range
          const normalized = Math.pow(level / 255, 0.8); // Apply slight compression
          const height = minBarHeight + normalized * (maxBarHeight - minBarHeight);
          
          return (
            <Box
              key={i}
              sx={{
                width: 3,
                height: `${height}px`,
                mx: 0.5,
                backgroundColor: '#ef4444',
                borderRadius: 1,
                transition: 'height 0.1s ease'
              }}
            />
          );
        })}
      </Box>
    );
  }, [audioLevel]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Audio element (hidden) */}
      <audio ref={audioRef} src={audioUrl || undefined} />

      {/* Microphone Error Message */}
      {microphoneError && (
        <Box sx={{
          width: "100%",
          p: 2,
          bgcolor: "#fee2e2",
          color: "#ef4444",
          borderRadius: 1,
          mb: 2,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1
        }}>
          <Typography sx={{ fontWeight: 500 }}>
            {microphoneError}
          </Typography>
          <Button
            onClick={() => {
              console.log("Retrying microphone setup");
              setupRecorder();
            }}
            variant="outlined"
            size="small"
            sx={{
              borderColor: "#ef4444",
              color: "#ef4444",
              textTransform: "none",
              "&:hover": {
                borderColor: "#dc2626",
                bgcolor: "rgba(239, 68, 68, 0.04)",
              }
            }}
          >
            <RefreshIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Retry
          </Button>
        </Box>
      )}

      {/* Browser Not Supported Message */}
      {!(typeof window !== 'undefined' && 'MediaRecorder' in window) && !microphoneError && (
        <Box sx={{
          width: "100%",
          p: 2,
          bgcolor: "#fee2e2",
          color: "#ef4444",
          borderRadius: 1,
          mb: 2,
          textAlign: "center"
        }}>
          <Typography sx={{ fontWeight: 500 }}>
            Audio recording is not supported in your browser.
          </Typography>
        </Box>
      )}

      {/* State containers */}
      {state === RecorderState.IDLE && (
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center",
          gap: 1,
          py: 2
        }}>
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              mb: 1
            }}
          >
            <Button
              onClick={() => {
                console.log("Record button clicked");
                if (!recorderRef.current) {
                  console.error("Recorder not ready yet");
                  setMicrophoneError("Microphone not ready. Please refresh and try again.");
                  return;
                }
                startRecording();
              }}
              disabled={!recorderRef.current || !!microphoneError}
              sx={{
                bgcolor: "#f3f4f6",
                color: "#ef4444",
                width: 60,
                height: 60,
                borderRadius: "50%",
                minWidth: 0,
                "&:hover": {
                  bgcolor: "#e5e7eb",
                  color: "#dc2626"
                },
                "&.Mui-disabled": {
                  bgcolor: "#f3f4f6",
                  color: "rgba(239, 68, 68, 0.5)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {recorderRef.current ? (
                <MicIcon sx={{ fontSize: 28 }} />
              ) : (
                <CircularProgress color="inherit" size={20} />
              )}
            </Button>
          </Box>
          
          <Typography sx={{ 
            fontSize: "0.875rem", 
            color: "rgb(75, 85, 99)", 
            textAlign: "center"
          }}>
            {recorderRef.current ? "Click to start recording" : "Initializing microphone..."}
          </Typography>
        </Box>
      )}

      {state === RecorderState.RECORDING && (
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center",
          py: 2 
        }}>
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            width: "100%", 
            mb: 0 
          }}>
            <Typography 
              sx={{ 
                fontSize: "1.75rem", 
                fontWeight: 500, 
                color: "#ef4444",
                fontFamily: "monospace",
                letterSpacing: "0.05em"
              }}
            >
              {formatDuration(elapsedTime)}
            </Typography>
          </Box>
          
          {/* Waveform visualization */}
          {renderWaveform()}
          
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            width: "100%",
            mb: 1
          }}>
            <Button
              onClick={stopRecording}
              sx={{
                bgcolor: "#ef4444",
                color: "white",
                width: 60,
                height: 60,
                borderRadius: "50%",
                minWidth: 0,
                "&:hover": {
                  bgcolor: "#dc2626",
                },
                transition: "all 0.2s ease"
              }}
            >
              <StopIcon sx={{ fontSize: 28 }} />
            </Button>
          </Box>
        </Box>
      )}

      {(state === RecorderState.RECORDED || state === RecorderState.PLAYING) && (
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center",
          py: 2 
        }}>
          <Box sx={{ 
            width: "85%", 
            height: 44, 
            bgcolor: "#f3f4f6", 
            borderRadius: "22px",
            display: "flex",
            alignItems: "center",
            px: 1,
            justifyContent: "space-between",
            mb: 2
          }}>
            <Button
              onClick={state === RecorderState.PLAYING ? pauseRecording : playRecording}
              sx={{
                minWidth: 36,
                width: 36,
                height: 36,
                p: 0,
                borderRadius: "50%",
                color: "#0a66c2",
                "&:hover": {
                  bgcolor: "rgba(10, 102, 194, 0.08)",
                },
              }}
            >
              {state === RecorderState.PLAYING ? (
                <PauseIcon sx={{ fontSize: 20 }} />
              ) : (
                <PlayIcon sx={{ fontSize: 20 }} />
              )}
            </Button>
            
            <Typography sx={{ 
              fontSize: "0.875rem", 
              color: "rgb(75, 85, 99)",
              fontFamily: "monospace",
              fontWeight: 600
            }}>
              {formatDuration(duration)}
            </Typography>
          </Box>
          
          <Stack 
            direction="row" 
            spacing={1.5} 
            sx={{ 
              width: "100%", 
              justifyContent: "center" 
            }}
          >
            <Button
              variant="outlined"
              onClick={resetRecording}
              sx={{
                borderColor: "rgb(229, 231, 235)",
                color: "rgb(107, 114, 128)",
                textTransform: "none",
                fontWeight: 500,
                py: 0.75,
                minWidth: 0,
                px: 1.5,
                "&:hover": {
                  borderColor: "rgb(209, 213, 219)",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Re-record
            </Button>
            
            {showUseThisButton && onAccept && (
              <Button
                variant="contained"
                onClick={acceptRecording}
                sx={{
                  bgcolor: "white",
                  color: "#0a66c2",
                  border: "1px solid #0a66c2",
                  textTransform: "none",
                  fontWeight: 500,
                  py: 0.75,
                  minWidth: 0,
                  px: 1.5,
                  "&:hover": {
                    bgcolor: "#f0f7ff",
                    borderColor: "#004182",
                  },
                }}
              >
                <CheckIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Use this
              </Button>
            )}
            
            {showDoneButton && (
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{
                  borderColor: "#0a66c2",
                  color: "#0a66c2",
                  textTransform: "none",
                  fontWeight: 500,
                  py: 0.75,
                  minWidth: 0,
                  px: 1.5,
                  "&:hover": {
                    borderColor: "#004182",
                    bgcolor: "rgba(10, 102, 194, 0.04)",
                  },
                }}
              >
                <CheckIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Done
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}; 